import React from "react";
import { BonsaiLogo } from "@/components/BonsaiLogo";
import { euro } from "@/lib/format";
import { API } from "@/lib/api";

function LineRow({ item, suffix }) {
  const onRequest = item.price_type === "onrequest" || (!item.price && item.price_note);
  const qty = item.quantity || 1;
  return (
    <div className="flex items-baseline justify-between py-3 border-b border-border">
      <div className="pr-4">
        <span className="font-semibold text-navy">{item.name}</span>
        {qty > 1 && <span className="text-muted-foreground text-sm"> × {qty}</span>}
      </div>
      <div className="text-right font-bold text-navy whitespace-nowrap">
        {onRequest ? (
          <span className="text-accent">{item.price_note || "Su richiesta"}</span>
        ) : (
          <>
            {euro((item.price || 0) * qty)}
            <span className="text-muted-foreground font-medium text-sm">{suffix}</span>
          </>
        )}
      </div>
    </div>
  );
}

const TRUST_BADGES = [
  { src: "/brand/badges/capterra.png", alt: "Capterra 5.0" },
  { src: "/brand/badges/getapp.png", alt: "GetApp User Reviews 5.0" },
  { src: "/brand/badges/software-advice.png", alt: "Software Advice 5.0" },
  { src: "/brand/badges/amazon-ads.png", alt: "Amazon Ads Verified Partner" },
  { src: "/brand/badges/amazon-software.png", alt: "Amazon Selling Partner Software Partner" },
];

function TrustBadges() {
  return (
    <div className="mt-10 pt-8 border-t border-border" data-testid="offer-trust-badges">
      <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold text-center mb-5">
        Partner certificati e recensioni verificate
      </p>
      <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-4">
        {TRUST_BADGES.map((b, i) => (
          <img
            key={i}
            src={b.src}
            alt={b.alt}
            className="h-12 w-auto object-contain"
            data-testid={`trust-badge-${i}`}
          />
        ))}
      </div>
    </div>
  );
}

function BundleRow({ item }) {
  const bd = Math.max(0, Math.min(100, Number(item.bundle_discount_percent) || 0));
  const finalPrice = (item.price || 0) * (1 - bd / 100);
  return (
    <div className="flex items-baseline justify-between py-3 border-b border-border">
      <div className="pr-4">
        <span className="font-semibold text-navy">{item.name}</span>
        {bd > 0 && <span className="ml-2 text-xs font-bold text-primary bg-primary/10 rounded-full px-2 py-0.5">−{bd}%</span>}
      </div>
      <div className="text-right whitespace-nowrap">
        {bd > 0 && <span className="text-muted-foreground text-sm line-through mr-2">{euro(item.price)}</span>}
        <span className="font-bold text-navy">{euro(finalPrice)}</span>
        <span className="text-muted-foreground font-medium text-sm">/mese</span>
      </div>
    </div>
  );
}

function BundleSection({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-6">
      <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-1">Bundle</h3>
      {items.map((it, i) => <BundleRow key={i} item={it} />)}
    </div>
  );
}

function Section({ title, items, suffix }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-6">
      <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-1">{title}</h3>
      {items.map((it, i) => (
        <LineRow key={i} item={it} suffix={suffix} />
      ))}
    </div>
  );
}

export default function OfferDocument({ offer }) {
  if (!offer) return null;
  const t = offer.totals || {};
  const monthlyShow = t.monthly_final != null ? t.monthly_final : t.monthly_net;
  const monthlyAddons = (offer.addons || []).filter((a) => a.price_type !== "oneoff");
  const oneoffAddons = (offer.addons || []).filter((a) => a.price_type === "oneoff");
  const logo = offer.agency_logo_url ? `${process.env.REACT_APP_BACKEND_URL}${offer.agency_logo_url}` : null;

  return (
    <div className="bg-white" data-testid="offer-document">
      {/* Header co-brand */}
      <div className="flex items-center justify-between gap-4 pb-6 border-b-2 border-navy">
        <BonsaiLogo size={30} />
        {logo && (
          <img src={logo} alt="Logo agenzia" className="h-12 max-w-[180px] object-contain" data-testid="offer-agency-logo" />
        )}
      </div>

      {/* Title */}
      <div className="py-8">
        <p className="text-xs uppercase tracking-widest text-primary font-bold">Offerta commerciale</p>
        <h1 className="text-3xl md:text-4xl font-extrabold text-navy tracking-tight mt-1">
          {offer.client_name}
        </h1>
        {offer.client_company && (
          <p className="text-muted-foreground mt-1">{offer.client_company}</p>
        )}
        <div className="flex flex-wrap gap-x-8 gap-y-1 mt-4 text-sm text-muted-foreground">
          {offer.agency_name && <span>Agenzia: <b className="text-navy">{offer.agency_name}</b></span>}
          <span>Commerciale: <b className="text-navy">{offer.agent_name || offer.agent_email}</b></span>
          <span>Data: <b className="text-navy">{(offer.created_at || "").slice(0, 10)}</b></span>
        </div>
      </div>

      {/* Items grid */}
      <div className="grid md:grid-cols-5 gap-8">
        <div className="md:col-span-3">
          {offer.hosting_plan && (
            <Section title="Licenza Hosting BSpanel" items={[offer.hosting_plan]} suffix="/mese" />
          )}
          <Section title="Moduli piattaforma" items={offer.modules} suffix="/mese" />
          <Section title="Add-on ricorrenti" items={monthlyAddons} suffix="/mese" />
          <BundleSection items={offer.bundles} />
          {offer.marketplaces && offer.marketplaces.length > 0 && (
            <div className="mb-6" data-testid="offer-marketplaces">
              <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2">Marketplace</h3>
              <div className="flex flex-wrap gap-2">
                {offer.marketplaces.map((m, i) => (
                  <span key={i} className="px-3 py-1 rounded-full bg-secondary text-navy text-sm font-semibold border border-border">{m.name}</span>
                ))}
              </div>
              {t.marketplace_cost > 0 && (
                <p className="text-sm text-muted-foreground mt-2">
                  Il primo marketplace e incluso · {t.marketplace_extra_count} aggiuntivi = <b className="text-navy">{euro(t.marketplace_cost)}/mese</b>
                </p>
              )}
            </div>
          )}
          <Section title="Integrazioni (una tantum)" items={offer.integrations} suffix="" />
          <Section title="Costi una tantum" items={oneoffAddons} suffix="" />
          {offer.notes && (
            <div className="mt-6">
              <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-1">Note</h3>
              <p className="text-navy whitespace-pre-line">{offer.notes}</p>
            </div>
          )}
        </div>

        {/* Totals */}
        <div className="md:col-span-2">
          <div className="border border-border rounded-xl overflow-hidden sticky top-6">
            <div className="bg-secondary px-6 py-4 border-b border-border">
              <p className="font-bold text-navy">Riepilogo</p>
            </div>
            <div className="p-6 space-y-3">
              {t.discount_percent > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Sconto moduli {t.discount_percent}%</span>
                  <span className="font-bold text-primary">− {euro(t.module_discount)}</span>
                </div>
              )}
              {t.agency_discount > 0 && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotale mensile</span>
                    <span className="font-semibold text-muted-foreground line-through">{euro(t.monthly_net)}</span>
                  </div>
                  <div className="flex justify-between text-sm" data-testid="offer-agency-discount">
                    <span className="text-muted-foreground">Sconto</span>
                    <span className="font-bold text-primary">− {euro(t.agency_discount)}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between items-baseline">
                <span className="text-muted-foreground text-sm">Totale mensile</span>
                <span className="text-2xl font-extrabold text-navy" data-testid="offer-total-monthly">
                  {euro(monthlyShow)}<span className="text-sm text-muted-foreground font-semibold">/mese</span>
                </span>
              </div>
              {t.oneoff_net > 0 && (
                <div className="flex justify-between items-baseline pt-3 border-t border-border">
                  <span className="text-muted-foreground text-sm">Una tantum</span>
                  <span className="text-xl font-extrabold text-navy" data-testid="offer-total-oneoff">
                    {euro(t.oneoff_net)}
                  </span>
                </div>
              )}
              <div className="pt-4 mt-2 bg-primary text-primary-foreground rounded-lg px-4 py-3 text-center -mx-2">
                <p className="text-xs uppercase tracking-wider opacity-90">Investimento primo mese</p>
                <p className="text-2xl font-extrabold">
                  {euro((monthlyShow || 0) + (t.oneoff_net || 0))}
                </p>
              </div>
              <p className="text-xs text-muted-foreground text-center pt-1">Prezzi IVA esclusa</p>
            </div>
          </div>
        </div>
      </div>

      <TrustBadges />

      <p className="mt-10 pt-6 border-t border-border text-xs text-muted-foreground">
        Tutti i prezzi si intendono IVA esclusa. Offerta indicativa, i prezzi possono variare
        in base al volume ordini e alle integrazioni richieste.
      </p>
    </div>
  );
}
